import { Card, Table, Tag, Typography } from 'antd';
import { PageShell, RefreshButton } from '../components/page-shell.js';
import type { JobSummary } from '../types.js';
import { useAdminQuery } from '../use-admin-query.js';

export function JobsPage() {
  const query = useAdminQuery<JobSummary[]>('/admin/v1/jobs?take=100');
  return (
    <PageShell
      title="Jobs"
      description="Operational metadata and provider attempts only. Inputs, outputs, prompts, and stream fragments are never shown here."
      action={
        <RefreshButton
          loading={query.loading}
          onClick={() => void query.refresh()}
        />
      }
      error={query.error}
    >
      <Card>
        <Table
          rowKey="id"
          loading={query.loading}
          dataSource={query.data ?? []}
          scroll={{ x: 1100 }}
          columns={[
            {
              title: 'Created',
              dataIndex: 'createdAt',
              render: (value: string) => new Date(value).toLocaleString(),
            },
            { title: 'Product', render: (_, row) => row.product.key },
            {
              title: 'Operation',
              render: (_, row) => row.operationProfile.key,
            },
            { title: 'Plan', dataIndex: 'planKey' },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (value: string) => (
                <Tag color={statusColor(value)}>{value}</Tag>
              ),
            },
            {
              title: 'Provider',
              dataIndex: 'selectedProvider',
              render: fallback,
            },
            { title: 'Model', dataIndex: 'selectedModel', render: fallback },
            {
              title: 'Attempts',
              render: (_, row) =>
                `${row.attemptCount} (${row.retryCount} retries, ${row.failoverCount} failovers)`,
            },
            {
              title: 'Error',
              render: (_, row) =>
                row.errorCode ? (
                  <Typography.Text type="danger">
                    {row.errorCode}
                  </Typography.Text>
                ) : (
                  '—'
                ),
            },
          ]}
        />
      </Card>
    </PageShell>
  );
}

function fallback(value: unknown): string {
  return typeof value === 'string' && value ? value : '—';
}

function statusColor(value: string): string {
  if (value === 'SUCCEEDED') return 'green';
  if (value === 'FAILED' || value === 'UNKNOWN_OUTCOME') return 'red';
  if (value === 'RUNNING') return 'blue';
  return 'default';
}
